import {
  CustomParameters,
  PaywallResult,
  PurchasableProduct,
} from '@accedo/one-plugin-sdk';
import { useCallback, useState } from 'react';

import { purchaseService } from '#/client/api';
import { PurchaseParams } from '#/components/Payment/types';
import { Errors } from '#/errors/errors';
import { getErrorMessageKeys, OneError } from '#/errors/oneError';
import { useRemoveQueryStringParams } from '#/hooks/useRemoveQueryStringParams';
import { useSendBuyEvent } from '#/hooks/useSendBuyEvent';
import { handlePaymentButtonClick } from '#/plugins/analytics/analyticsUtils';
import { convertSdkTypeToCore } from '#/plugins/paywall/utils';
import { ignoreError } from '#/service/utils';
import { authActions, useAuthStore } from '#/store/auth/auth.store';
import { modalActions } from '#/store/modal/modal.store';
import { usePaywallStore } from '#/store/paywall/paywall.store';
import { tb } from '#/store/translation/translation.store';

import { Actions, SubscriptionParams } from '../PlanPurchaseFlow.types';

interface Props {
  handleClose: Actions['handleClose'];
  customParameters?: CustomParameters;
  selectedProduct?: PurchasableProduct;
  onPurchaseComplete: (result: PaywallResult) => void;
}

interface Payment {
  isSuccessful: boolean;
  isExternal?: boolean;
}

export const usePurchaseProductStep = ({
  handleClose,
  customParameters,
  onPurchaseComplete,
  selectedProduct,
}: Props) => {
  const { paywallApi } = usePaywallStore();
  const { awaitingEntitlements } = useAuthStore();
  const { removeQueryParams } = useRemoveQueryStringParams();
  const { sendBuyEvent } = useSendBuyEvent();

  const [payment, setPayment] = useState<Payment>();
  const [voucherCode, setVoucherCode] = useState<string>();
  const [discountedPrice, setDiscountedPrice] = useState<string>();
  const [isPaymentDetailsRequired, setIsPaymentDetailsRequired] = useState(
    selectedProduct?.isPaymentDetailsRequired ?? true
  );

  const hasOwnUi = paywallApi.hasOwnUi();

  const handlePurchaseComplete = useCallback(
    ({ isSuccessful, isExternal }: Payment) => {
      setPayment({ isSuccessful, isExternal });
      onPurchaseComplete(
        isSuccessful ? PaywallResult.Payed : PaywallResult.Aborted
      );
    },
    [onPurchaseComplete]
  );

  const handlePurchase = useCallback(
    async (
      { card, paymentMethod }: PurchaseParams,
      buyButtonText: string | undefined
    ) => {
      handlePaymentButtonClick({
        paymentMethod,
        label: tb(buyButtonText),
        product: selectedProduct,
        discountedPrice,
        voucherCode,
      });

      if (!paywallApi.purchaseProduct) {
        throw new OneError(
          Errors.WEB_INTEGRATION_GENERAL_OPERATION_NOT_SUPPORTED,
          { extras: { method: 'purchaseProduct' } }
        );
      }

      if (!selectedProduct) {
        throw new OneError(Errors.PURCHASE_UNKNOWN_EXISTING_SUBSCRIPTION);
      }

      const { id, type } = selectedProduct;

      purchaseService.trackPurchase({
        status: 'PENDING',
        message: 'Initializing purchase with integration.',
        orderDetails: {
          paymentMethod: convertSdkTypeToCore(paymentMethod),
          productId: selectedProduct.id,
          couponCodes: [voucherCode],
        },
        attributionTag: customParameters?.vendorTag,
      });

      try {
        const response = await paywallApi.purchaseProduct(id, type, {
          card,
          paymentMethod,
          voucherCode,
          customParameters,
        });

        // `res` here contains the actual price paid, order id, renewal date, etc.
        console.debug(
          'Product purchase successful, potentially finalized externally.',
          response
        );

        const { isExternal } = response;

        if (isExternal) {
          purchaseService.trackPurchase({
            status: 'PENDING',
            message:
              'Purchase requires completion externally, re-directing the user.',
            orderDetails: {
              paymentMethod: convertSdkTypeToCore(paymentMethod),
              productId: selectedProduct.id,
              couponCodes: [voucherCode],
            },
            attributionTag: customParameters?.vendorTag,
          });

          // If the payment was finalized externally, we don't show success yet, instead we show loading-redirect screen.
          // The user will be redirected to the external payment provider to complete purchase.
          handlePurchaseComplete({ isSuccessful: false, isExternal });

          return response;
        }

        purchaseService.trackPurchase({
          status: 'SUCCESS',
          message: 'Purchase was successful.',
          orderDetails: {
            transactionId: response?.transactionId,
            paymentMethod: convertSdkTypeToCore(paymentMethod),
            productId: selectedProduct.id,
            couponCodes: [voucherCode],
          },
          attributionTag: customParameters?.vendorTag,
        });

        try {
          // After purchase, we need to refresh profile data, so that the user will be able to consume the content
          // right away. It does mean that the spinner will be significantly longer, because we will wait for
          // the successful resolution (or up to X seconds as defined in the action below)
          await authActions.refreshProfileAfterEntitlementPurchase();
        } finally {
          sendBuyEvent({
            ...response,
            duration: selectedProduct?.duration,
          }).catch(ignoreError);

          removeQueryParams([
            SubscriptionParams.Voucher,
            SubscriptionParams.Product,
          ]);
        }

        handlePurchaseComplete({ isSuccessful: true, isExternal });

        return response;
      } catch (err) {
        console.error('Product purchase failed', err);

        purchaseService.trackPurchase({
          status: 'FAILURE',
          message: `Purchase attempt failed.  Reason: ${JSON.stringify(err)}`,
          orderDetails: {
            paymentMethod: convertSdkTypeToCore(paymentMethod),
            productId: selectedProduct.id,
            couponCodes: [voucherCode],
          },
          attributionTag: customParameters?.vendorTag,
        });

        throw err;
      }
    },
    [
      paywallApi,
      selectedProduct,
      customParameters,
      discountedPrice,
      voucherCode,
      sendBuyEvent,
      removeQueryParams,
      handlePurchaseComplete,
    ]
  );

  const handlePaymentFailure = useCallback(
    (error: unknown) => {
      console.error('Payment error', error);

      // Resolve a specific message from the error code when available (e.g. a voucher
      // usage-limit error mapped by the paywall plugin at apply time), falling back to
      // the generic payment error message.
      const { keys, context } = getErrorMessageKeys(error, 'iap.error.message');

      modalActions.showModal({
        title: tb('iap.error.title'),
        message: tb(keys, context),

        // the drop-in form doesn't support retrying in place
        onDismiss: hasOwnUi ? handleClose : undefined,
      });
    },
    [handleClose, hasOwnUi]
  );

  const onVoucherChange = useCallback(
    async (productId: string, voucherCode: string) => {
      if (!paywallApi.validateVoucher) {
        throw new Error('validateVoucher is not implemented');
      }

      return paywallApi
        .validateVoucher(voucherCode, productId)
        .then((res) => {
          setVoucherCode(voucherCode);
          setDiscountedPrice(res.discountedPrice);
          setIsPaymentDetailsRequired(res.isPaymentDetailsRequired);

          return res;
        })
        .catch((err) => {
          setVoucherCode(undefined);
          throw err;
        });
    },
    [paywallApi]
  );

  const onVoucherRemove = useCallback(async () => {
    await paywallApi.removeVoucher?.();
    setVoucherCode(undefined);
    setDiscountedPrice(undefined);
    setIsPaymentDetailsRequired(
      selectedProduct?.isPaymentDetailsRequired ?? true
    );
  }, [paywallApi, selectedProduct?.isPaymentDetailsRequired]);

  return {
    payment,
    voucherCode,
    awaitingEntitlements,
    hasOwnUi,
    isPaymentDetailsRequired,
    handlePurchaseComplete,
    handlePurchase,
    handlePaymentFailure,
    onVoucherChange,
    onVoucherRemove,
  };
};
